import { callApi } from "@/utils/http-utils";

export type ModalityCreatePayload = {
  name: string;
  description: string;
  keyFeatures?: string | null;
  status?: boolean;
  isManualEntry?: boolean;
  changeDescription?: string | null;
};

const BASE_MODALITY_PROMPT =
  `You are an expert in learning modalities. Consolidate the modalites found in the <context> section to a JSON array, removing duplicates. ` +
  `Map the modalities found to the appropriate modality cluster name using the cluster definitions in the <clusters> section. ` +
  `Each JSON object must include: ` +
  `- id (exactly as given in <context>) ` +
  `- name ` +
  `- description ` +
  `- primaryCluster (exact cluster name from <clusters> section) ` +
  `- primaryClusterId (exact cluster id number from <clusters> section) ` +
  `BOTH primaryCluster AND primaryClusterId are REQUIRED for each object. ` +
  `RETURN JSON ONLY. DO NOT INCLUDE ANY LINE BREAKS "\\n". `;

// Function to get dynamic prompt context from database
export async function getPromptContext() {
  const res = await callApi(`data-api/prompt-context`, { method: "GET" });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as {
    clustersXml: string;
    modalitiesJsonl: string[];
  };
}

// Function to build complete prompt with dynamic cluster data
export function buildDynamicPrompt(clustersXml: string) {
  return BASE_MODALITY_PROMPT + clustersXml;
}

export async function listModalities(limit = 20, offset = 0) {
  const res = await callApi(
    `data-api/modalities?limit=${limit}&offset=${offset}`,
    { method: "GET" },
  );
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    throw new Error(`Expected JSON but got ${ct}. ${text.slice(0, 200)}`);
  }
  const body = await res.json();
  const data = Array.isArray(body)
    ? body
    : body.data || body.items || body.results || [];
  return { totalCount: body.totalCount ?? data.length, data };
}

export async function createModality(payload: ModalityCreatePayload) {
  const res = await callApi(`data-api/modalities`, {
    method: "POST",
    body: payload, // omit status to default to inactive on the backend, or send status:false
    accept: "application/json",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function patchModalityStatus(
  id: number | string,
  status: boolean,
) {
  const res = await callApi(`data-api/modalities/${id}`, {
    method: "PATCH",
    body: { status },
    accept: "application/json",
  });
  if (!res.ok) throw new Error(await res.text());
}

// Updated to use dynamic prompt - remove the prompt parameter
export async function runModalityMapping(contextString: string) {
  // Get the dynamic context with actual cluster IDs from database
  const { clustersXml } = await getPromptContext();

  // Build the prompt with real cluster data
  const dynamicPrompt = buildDynamicPrompt(clustersXml);

  const res = await callApi(`data-api/one-time`, {
    method: "POST",
    body: { contextString, prompt: dynamicPrompt },
    accept: "application/json",
  });
  return res.text();
}

export async function applyModalityMappings(
  results: Array<{
    id: number;
    primaryClusterId?: number;
    primaryCluster?: string;
  }>,
) {
  if (!Array.isArray(results) || results.length === 0) {
    throw new Error("No mapping results to apply.");
  }

  const needsLookup = results.some((r) => !r.primaryClusterId);

  let nameToId: Record<string, number> = {};
  if (needsLookup) {
    // Fetch clusters once and build a case-insensitive lookup
    const clustersRes = await callApi(`data-api/clusters?limit=200&offset=0`, {
      method: "GET",
    });
    if (!clustersRes.ok) {
      const errorText = await clustersRes.text();
      throw new Error(`Failed to fetch clusters: ${errorText}`);
    }
    const clustersBody = (await clustersRes.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    for (const c of clustersBody.data ?? []) {
      nameToId[c.name.trim().toLowerCase()] = c.id;
    }
  }

  const cleanPayload = results
    .map((r) => {
      const id = Number(r.id);
      let primaryClusterId = r.primaryClusterId;

      // If no direct ID, try to look up by name
      if (!primaryClusterId && r.primaryCluster) {
        primaryClusterId = nameToId[r.primaryCluster.trim().toLowerCase()];
      }

      return Number.isFinite(id) && Number.isFinite(primaryClusterId)
        ? { id, primaryCluster: String(r.primaryCluster) }
        : null;
    })
    .filter((x): x is { id: number; primaryCluster: string } => x !== null);

  if (cleanPayload.length === 0) {
    throw new Error(
      "No valid mapping results after processing. Check cluster names/ids.",
    );
  }

  const res = await callApi(`data-api/modality-cluster-mapping-runs`, {
    method: "POST",
    body: { results: cleanPayload },
    accept: "application/json",
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Mapping request failed: ${errorText}`);
  }

  return res.json();
}
